import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Eye, Hourglass, History, RotateCcw, Pencil, Search, X, LayoutGrid, Rows3, Download, PartyPopper, AlertTriangle, Lock } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Badge, PriorityBadge } from '@/components/ui/Badge';
import { Table } from '@/components/ui/Table';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { listEnquiries, reopenOrder, enquiriesExportPath, enquiryLinesExportPath, type Enquiry } from '@/api/enquiry';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { EnqReviewModal } from './EnqReviewModal';
import { EnquiryFormModal } from '../enquiry/EnquiryFormModal';
import { orderRef } from '@/lib/orderRef';

// Stages where the order form is still editable (before a DO exists).
const EDITABLE_STAGES = new Set(['new', 'reviewed']);

// Stages eligible to reopen: approved (no DO yet), or with a DO that hasn't shipped.
const REOPENABLE_STAGES = new Set([
  'reviewed',
  'do-created',
  'credit-pending',
  'credit-approved',
  'pick-pending',
  'pick-done',
  'packing-done',
]);

const CREDIT_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

const STAGE_TONE: Record<string, 'muted' | 'info' | 'warning' | 'success' | 'danger' | 'accent'> = {
  new: 'info',
  reviewed: 'accent',
  'do-created': 'accent',
  'credit-pending': 'warning',
  'credit-approved': 'success',
  'credit-rejected': 'danger',
  'pick-pending': 'accent',
  'pick-done': 'accent',
  'packing-done': 'accent',
  'grn-out': 'success',
  invoiced: 'success',
  'gate-out': 'success',
  fulfilled: 'success',
  cancelled: 'danger',
};

// Human-friendly labels for the History "Stage" badge. A cancelled order (set by
// either Cancel or Reject) is shown as "Discarded" so it's unambiguous the order
// was deleted/discarded rather than still in flight.
const STAGE_LABEL: Record<string, string> = {
  cancelled: 'Discarded',
  'credit-rejected': 'Credit Rejected',
};

// Full-text haystack for an order — every field shown on the card / table row,
// including each line item, so the search bar matches on anything visible.
function orderHaystack(e: Enquiry): string {
  return [
    e.enqNo,
    e.party?.name,
    e.party?.customerType,
    e.salesRep,
    e.customerPoRef,
    e.stage,
    STAGE_LABEL[e.stage],
    e.creditStatus && `credit ${e.creditStatus}`,
    e.priority,
    formatDate(e.enqDate),
    formatDate(e.expectedDelivery ?? ''),
    String(e.estOrderValue ?? ''),
    ...e.items.flatMap((it) => [it.itemName, (it as { itemCode?: string }).itemCode, String(it.qty), it.unit, it.stockStatus]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchOrder(e: Enquiry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = orderHaystack(e);
  return q.split(/\s+/).every((t) => hay.includes(t));
}

/** Pending-queue kinds — the same split as the dashboard's Sales pipeline. */
type PendingKind = 'all' | 'new' | 'partial-dispatch' | 'partial-approval';
const PENDING_KINDS: { key: PendingKind; label: string; empty: string }[] = [
  { key: 'all', label: 'All pending', empty: 'pending' },
  { key: 'new', label: 'New orders', empty: 'new' },
  { key: 'partial-dispatch', label: 'Partly dispatched — rest to send', empty: 'partly dispatched' },
  { key: 'partial-approval', label: 'Partly approved — rest to approve', empty: 'partly approved' },
];

/** Sort orders for the pending queue — e.g. partly dispatched orders by how much is still to go. */
type PendingSort = 'default' | 'pending-desc' | 'pending-asc' | 'date-asc' | 'date-desc' | 'value-desc';
const PENDING_SORTS: { key: PendingSort; label: string }[] = [
  { key: 'default', label: 'Sort: default' },
  { key: 'pending-desc', label: 'Most units pending' },
  { key: 'pending-asc', label: 'Least units pending' },
  { key: 'date-asc', label: 'Oldest order first' },
  { key: 'date-desc', label: 'Newest order first' },
  { key: 'value-desc', label: 'Highest order value' },
];
/** Units still to dispatch on an order (ordered − dispatched). */
const unitsPending = (e: Enquiry) => e.items.reduce((s, it) => s + Math.max(0, Number(it.qty || 0) - Number(it.dispatchedQty || 0)), 0);
/**
 * Value still to dispatch: each line's undispatched share of its value, scaled to the order
 * value (so an order-level discount carries through). Same rule as the Excel export.
 */
const remainingValue = (e: Enquiry) => {
  const sum = e.items.reduce((a, it) => a + Number(it.estValue || 0), 0);
  const scale = sum > 0 ? Number(e.estOrderValue || 0) / sum : 0;
  const v = e.items.reduce((a, it) => {
    const qty = Number(it.qty || 0);
    return qty > 0 ? a + (Math.max(0, qty - Number(it.dispatchedQty || 0)) / qty) * Number(it.estValue || 0) * scale : a;
  }, 0);
  return Math.round(v * 100) / 100;
};

/** A dispatch balance still to go wins over a part-approved line; otherwise it's a new order. */
function pendingKindOf(e: Enquiry): Exclude<PendingKind, 'all'> {
  if (e.balancePending) return 'partial-dispatch';
  if (e.items.some((it) => it.approvedQty != null && Number(it.approvedQty) < Number(it.qty))) return 'partial-approval';
  return 'new';
}

export function EnqReviewPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const docViewer = useDocViewer();
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const [editOrder, setEditOrder] = useState<Enquiry | null>(null);
  const [tab, setTab] = useState<'pending' | 'history'>('pending');
  const [pendingView, setPendingView] = useState<'cards' | 'table'>('cards');
  const [q, setQ] = useState('');
  const [pendingKind, setPendingKind] = useState<PendingKind>('all');
  const [pendingSort, setPendingSort] = useState<PendingSort>('default');

  const reopenMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => reopenOrder(id, reason),
    onSuccess: (e) => {
      toast.push(`${e.enqNo} reopened — edit it from the Pending queue`, 'success');
      qc.invalidateQueries({ queryKey: ['enquiries'] });
    },
    onError: (err) => toast.push(apiError(err, 'Reopen failed'), 'error'),
  });

  // Export every row of the current tab (server-side, no cap). When the search
  // box has a term, the export is narrowed to matching orders too — so a search
  // for a client exports only that client's orders.
  function exportFilter() {
    // Pending: export exactly what's on screen — the dropdown (all / new / partly …), the
    // search and the sort — by sending those orders' ids in their on-screen order.
    if (tab === 'pending') return { reviewQueue: true, flowType: 'order' as const, ids: shownPending.map((e) => e.id).join(',') || '0' };
    const base = { notStage: 'new', flowType: 'order' as const };
    const s = q.trim();
    return s ? { ...base, search: s } : base;
  }
  /** File-name part for the pending view, e.g. "Partly_dispatched". */
  const viewTag = pendingKind === 'all' ? 'Pending' : (PENDING_KINDS.find((k) => k.key === pendingKind)?.empty ?? 'Pending').replace(/\s+/g, '_').replace(/^./, (c) => c.toUpperCase());
  function onExport() {
    if (tab === 'pending' && shownPending.length === 0) { toast.push('Nothing to export in this view', 'warning'); return; }
    const name = tab === 'pending' ? `GRHAMOY_Order_Review_${viewTag}.xlsx` : 'GRHAMOY_Order_Review_History.xlsx';
    downloadExcel(enquiriesExportPath(exportFilter()), name).catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }
  function onExportLines() {
    if (tab === 'pending' && shownPending.length === 0) { toast.push('Nothing to export in this view', 'warning'); return; }
    const name = tab === 'pending' ? `GRHAMOY_Order_Review_${viewTag}_LineItems.xlsx` : 'GRHAMOY_Order_Review_History_LineItems.xlsx';
    downloadExcel(enquiryLinesExportPath(exportFilter()), name, 120000).catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }
  // Download one Sales Order as an invoice-style Excel (same data as the SO PDF).
  function onSoExcel(id: number, enqNo: string) {
    downloadExcel(`/docs/sales-order-review/${id}/export.xlsx`, `GRHAMOY_Sales_Order_${enqNo}.xlsx`, 120000)
      .catch((err) => toast.push(apiError(err, 'Sales Order export failed'), 'error'));
  }

  async function onReopen(e: Enquiry) {
    const reason = await ask({
      title: e.stage === 'reviewed' ? `Reopen ${e.enqNo} for review?` : `Reopen ${e.enqNo} for editing?`,
      message:
        e.stage === 'reviewed'
          ? 'This clears its approval (the approved stock is freed for other orders) and sends the order back to Pending Review as editable.'
          : 'This voids its dispatch order, releases the reserved stock, and sends the order back to Pending Review as editable.',
      label: 'Reason',
      multiline: true,
      tone: 'danger',
      confirmLabel: 'Reopen',
    });
    if (!reason?.trim()) return;
    reopenMutation.mutate({ id: e.id, reason: reason.trim() });
  }

  // Pending — orders that still need the reviewer's decision.
  const pendingQuery = useQuery({
    queryKey: ['enquiries', { reviewQueue: true, flowType: 'order', pageSize: 200 }],
    queryFn: () => listEnquiries({ reviewQueue: true, flowType: 'order', pageSize: 200 }),
    refetchInterval: 60_000,
    enabled: tab === 'pending',
  });

  // History — every firm order that has moved past 'new' (reviewed, in DO,
  // credit-checked, picked, packed, shipped, cancelled…). notStage='new' does
  // the heavy lifting server-side so we don't overfetch.
  const historyQuery = useQuery({
    queryKey: ['enquiries', { notStage: 'new', flowType: 'order', pageSize: 200 }],
    queryFn: () => listEnquiries({ notStage: 'new', flowType: 'order', pageSize: 200 }),
    refetchInterval: 60_000,
    enabled: tab === 'history',
  });

  const pending = pendingQuery.data?.data ?? [];
  const history = historyQuery.data?.data ?? [];
  const isFetching = tab === 'pending' ? pendingQuery.isFetching : historyQuery.isFetching;

  const kindCount = useMemo(() => {
    const c: Record<PendingKind, number> = { all: pending.length, new: 0, 'partial-dispatch': 0, 'partial-approval': 0 };
    for (const e of pending) c[pendingKindOf(e)] += 1;
    return c;
  }, [pending]);
  const shownPending = useMemo(() => {
    const rows = pending.filter((e) => (pendingKind === 'all' || pendingKindOf(e) === pendingKind) && matchOrder(e, q));
    if (pendingSort === 'default') return rows;
    const day = (e: Enquiry) => new Date(e.enqDate).getTime() || 0;
    const cmp: Record<Exclude<PendingSort, 'default'>, (a: Enquiry, b: Enquiry) => number> = {
      'pending-desc': (a, b) => unitsPending(b) - unitsPending(a),
      'pending-asc': (a, b) => unitsPending(a) - unitsPending(b),
      'date-asc': (a, b) => day(a) - day(b),
      'date-desc': (a, b) => day(b) - day(a),
      'value-desc': (a, b) => Number(b.estOrderValue || 0) - Number(a.estOrderValue || 0),
    };
    return [...rows].sort(cmp[pendingSort]);
  }, [pending, q, pendingKind, pendingSort]);
  const shownHistory = useMemo(() => history.filter((e) => matchOrder(e, q)), [history, q]);

  return (
    <div>
      <SectionHeader
        title="Order Review"      />

      {/* Pending Review / History — the page view, above the filter bar. */}
      <div className="mb-2">
        <div className="inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1">
          <button
            type="button"
            onClick={() => setTab('pending')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
              (tab === 'pending'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Orders awaiting your review decision"
          >
            <Hourglass size={13} strokeWidth={2.25} />
            Pending Review
            {pending.length > 0 && tab !== 'pending' && (
              <span className="ml-1 rounded-full bg-accent/20 px-1.5 py-0.5 text-[10px] font-black text-accent">
                {pending.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('history')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
              (tab === 'history'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Orders already reviewed or otherwise moved forward"
          >
            <History size={13} strokeWidth={2.25} />
            History
          </button>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-2 backdrop-blur-xl">
        <div className="relative min-w-[180px] flex-1 sm:max-w-sm">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search — #, customer, rep, item, stage…"
            className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] py-1.5 pl-8 pr-8 text-[11px] font-semibold text-[var(--text-primary)] shadow-sm outline-none transition focus:border-accent sm:text-xs"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="Clear search"
            >
              <X size={13} />
            </button>
          )}
        </div>
        {tab === 'pending' && (
          <select
            value={pendingKind}
            onChange={(e) => setPendingKind(e.target.value as PendingKind)}
            aria-label="Show pending orders of this kind"
            title="Separate new orders from partly dispatched / partly approved orders"
            className={`rounded-lg border bg-[var(--bg-secondary)] py-1.5 pl-2 pr-7 text-[11px] font-bold shadow-sm outline-none transition focus:border-accent sm:text-xs ${pendingKind === 'all' ? 'border-[var(--border-color)] text-[var(--text-primary)]' : 'border-accent text-accent'}`}
          >
            {PENDING_KINDS.map((k) => (
              <option key={k.key} value={k.key}>{k.label} ({kindCount[k.key]})</option>
            ))}
          </select>
        )}
        {tab === 'pending' && (
          <select
            value={pendingSort}
            onChange={(e) => setPendingSort(e.target.value as PendingSort)}
            aria-label="Sort pending orders"
            title="Sort the orders — e.g. partly dispatched ones by units still to go"
            className={`rounded-lg border bg-[var(--bg-secondary)] py-1.5 pl-2 pr-7 text-[11px] font-bold shadow-sm outline-none transition focus:border-accent sm:text-xs ${pendingSort === 'default' ? 'border-[var(--border-color)] text-[var(--text-primary)]' : 'border-accent text-accent'}`}
          >
            {PENDING_SORTS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={onExport}
          className="ml-auto inline-flex items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:gap-1.5 sm:px-3 sm:text-xs"
          title={q.trim() ? `Export orders matching “${q.trim()}” to Excel` : `Export all ${tab === 'pending' ? 'pending' : 'processed'} orders to Excel`}
        >
          <Download size={13} /> Export
        </button>
        <button
          type="button"
          onClick={onExportLines}
          className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:gap-1.5 sm:px-3 sm:text-xs"
          title={q.trim() ? `Export line items for orders matching “${q.trim()}”` : 'Export every order LINE ITEM with its live warehouse availability & stock status'}
        >
          <Download size={13} /> Export Line Items
        </button>
        {tab === 'pending' && (
          <div className="inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1">
            <button
              type="button"
              onClick={() => setPendingView('cards')}
              className={
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
                (pendingView === 'cards' ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-muted)] hover:text-accent')
              }
              title="Card view"
            >
              <LayoutGrid size={13} strokeWidth={2.25} /> Cards
            </button>
            <button
              type="button"
              onClick={() => setPendingView('table')}
              className={
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
                (pendingView === 'table' ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-muted)] hover:text-accent')
              }
              title="Table view"
            >
              <Rows3 size={13} strokeWidth={2.25} /> Table
            </button>
          </div>
        )}
      </div>

      {tab === 'pending' ? (
        isFetching && pending.length === 0 ? (
          <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center text-sm text-[var(--text-muted)]">
            Loading…
          </div>
        ) : pending.length === 0 ? (
          <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
            <PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No firm orders pending review — sales reps click "Generate Order" on the Enquiry Capture page once a customer accepts, and it appears here.
          </div>
        ) : shownPending.length === 0 ? (
          <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
            {q ? <>No orders match “{q}”{pendingKind !== 'all' ? ' in this view' : ''}.</> : `No ${PENDING_KINDS.find((k) => k.key === pendingKind)?.empty} orders.`}
          </div>
        ) : pendingView === 'table' ? (
          <Table<Enquiry>
            paginated
            storageKey="enquiry-review-pending"
            title="Pending Review"
            count={q || pendingKind !== 'all' ? `Showing ${shownPending.length} of ${pending.length}` : `${shownPending.length}`}
            rows={shownPending}
            rowKey={(r) => r.id}
            columns={[
              { key: 'enqNo', header: 'ENQ #', minWidth: '130px', cell: (r) => <span className="font-extrabold text-accent" title={r.enqNo}>{orderRef(r)}</span> },
              { key: 'party', header: 'Customer', minWidth: '180px', cell: (r) => <span className="font-bold">{r.party.name}</span> },
              { key: 'category', header: 'Category', minWidth: '130px', cell: (r) => r.party.customerType ?? '—' },
              { key: 'salesRep', header: 'Sales Rep', minWidth: '110px', cell: (r) => r.salesRep },
              { key: 'date', header: 'Date', minWidth: '105px', cell: (r) => formatDate(r.enqDate) },
              { key: 'items', header: 'Items', minWidth: '60px', align: 'right', cell: (r) => r.items.length },
              { key: 'value', header: 'Order Value', minWidth: '120px', align: 'right', cell: (r) => formatINRSymbol(r.estOrderValue) },
              { key: 'remaining', header: 'Remaining Value', minWidth: '130px', align: 'right', cell: (r) => formatINRSymbol(remainingValue(r)) },
              { key: 'priority', header: 'Priority', minWidth: '90px', cell: (r) => <PriorityBadge priority={r.priority} /> },
              { key: 'credit', header: 'Credit', minWidth: '90px', cell: (r) => (r.creditStatus ? <Badge tone={CREDIT_TONE[r.creditStatus]}>{r.creditStatus}</Badge> : '—') },
              {
                key: 'stock', header: 'Stock', minWidth: '80px',
                cell: (r) => (r.items.some((it) => it.stockStatus === 'RED')
                  ? <Badge tone="danger">Short</Badge>
                  : <Badge tone="success">OK</Badge>),
              },
              {
                key: 'actions', header: '', minWidth: '200px',
                cell: (r) => (
                  <div className="flex flex-wrap justify-center gap-1.5">
                    <Button variant="primary" size="sm" onClick={() => setReviewId(r.id)} disabled={r.party.customerType === 'Lead'}
                      title={r.party.customerType === 'Lead' ? 'Convert party to Customer first' : 'Review this enquiry'}>
                      Review →
                    </Button>
                    {EDITABLE_STAGES.has(r.stage) && (
                      <Button variant="glass" size="sm" onClick={() => setEditOrder(r)} title="Edit order">
                        <span className="inline-flex items-center gap-1"><Pencil size={13} /> Edit</span>
                      </Button>
                    )}
                    <Button variant="glass" size="sm" onClick={() => docViewer.open('sales-order-review', r.id, r.enqNo)} title="Print / view Sales Order receipt">
                      <FileText size={13} />
                    </Button>
                    <Button variant="glass" size="sm" onClick={() => onSoExcel(r.id, r.enqNo)} title="Download Sales Order as Excel">
                      <Download size={13} />
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-2 sm:gap-4">
            {shownPending.map((e: Enquiry) => {
              // Partially approved: a line was approved for less than its ordered
              // qty, so an un-approved balance still awaits a later review. Distinct
              // from balancePending (already dispatched, balance remains).
              const partiallyApproved = e.items.some(
                (it) => it.approvedQty != null && Number(it.approvedQty) < Number(it.qty),
              );
              const isPartial = e.balancePending || partiallyApproved;
              return (
              <div
                key={e.id}
                className="flex flex-col gap-3 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-3 backdrop-blur-xl sm:p-5"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-base font-black text-accent" title={e.enqNo}>{orderRef(e)}</div>
                    <div className="text-sm font-bold">{e.party.name}</div>
                    <div className="text-[11px] text-[var(--text-muted)]">
                      {e.party.customerType ?? 'Customer'} · {formatDate(e.enqDate)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <PriorityBadge priority={e.priority} />
                    {e.creditStatus && (
                      <Badge tone={CREDIT_TONE[e.creditStatus]}>Credit {e.creditStatus}</Badge>
                    )}
                    {isPartial && (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-500">
                        ⏳ {partiallyApproved ? 'Partially approved' : 'Partial'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Meta label="Sales Rep">{e.salesRep}</Meta>
                  <Meta label="Customer PO">{e.customerPoRef ?? '—'}</Meta>
                  <Meta label="Expected">{formatDate(e.expectedDelivery ?? '') || '—'}</Meta>
                  <Meta label="Order Value">{formatINRSymbol(e.estOrderValue)}</Meta>
                  <Meta label="Remaining Value">
                    <span className={remainingValue(e) < Number(e.estOrderValue || 0) - 0.5 ? 'text-amber-500' : undefined}>{formatINRSymbol(remainingValue(e))}</span>
                  </Meta>
                </div>

                <div className="rounded-md border border-[var(--border-color)] bg-white/[0.02] p-2.5 text-[12px] text-[var(--text-secondary)]">
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                    Items ({e.items.length})
                  </div>
                  <ul className="space-y-0.5">
                    {e.items.slice(0, 4).map((it) => (
                      <li key={it.id} className="flex items-center justify-between gap-2">
                        <span className="min-w-0 flex-1 truncate">
                          • {it.itemName} — <strong>{it.qty}</strong> {it.unit}
                        </span>
                        {it.stockStatus === 'RED' && (
                          <span className="flex-shrink-0 rounded-full bg-danger/15 px-1.5 text-[10px] font-bold text-danger">Short</span>
                        )}
                      </li>
                    ))}
                    {e.items.length > 4 && (
                      <li className="text-[var(--text-muted)]">…and {e.items.length - 4} more</li>
                    )}
                  </ul>
                </div>

                {e.balancePending ? (() => {
                  const total = e.items.reduce((s, it) => s + Number(it.qty || 0), 0);
                  const done = e.items.reduce((s, it) => s + Number(it.dispatchedQty || 0), 0);
                  return (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-2.5 py-1 text-[11px] font-semibold text-amber-500">
                      ⏳ Partially dispatched — {done} of {total} units done · {total - done} pending. Review to raise the balance DO.
                    </div>
                  );
                })() : partiallyApproved ? (() => {
                  const total = e.items.reduce((s, it) => s + Number(it.qty || 0), 0);
                  const approved = e.items.reduce(
                    (s, it) => s + (it.approvedQty != null ? Number(it.approvedQty) : Number(it.qty || 0)),
                    0,
                  );
                  return (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-2.5 py-1 text-[11px] font-semibold text-amber-500">
                      ⏳ Partially approved — {approved} of {total} units approved · {total - approved} awaiting approval. Approve the balance when stock is back.
                    </div>
                  );
                })() : null}

                {e.items.some((it) => it.stockStatus === 'RED') && (
                  <div className="rounded-md border border-warning/30 bg-warning/[0.08] px-2.5 py-1 text-[11px] text-warning">
                    <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> Stock shortage on some lines
                  </div>
                )}

                {e.party.customerType === 'Lead' && (
                  <div className="rounded-md border border-warning/40 bg-warning/[0.10] px-2.5 py-1 text-[11px] font-semibold text-warning">
                    <Lock size={13} className="mr-1 inline-block align-[-2px]" /> Blocked — party is still a Lead. Convert them to a Customer in Party Master to review.
                  </div>
                )}

                <div className="flex gap-1.5">
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    onClick={() => setReviewId(e.id)}
                    disabled={e.party.customerType === 'Lead'}
                    title={
                      e.party.customerType === 'Lead'
                        ? 'Convert party to Customer first (Party Master → Convert)'
                        : 'Review this enquiry'
                    }
                  >
                    Review →
                  </Button>
                  {EDITABLE_STAGES.has(e.stage) && (
                    <Button
                      variant="glass"
                      size="sm"
                      onClick={() => setEditOrder(e)}
                      title="Edit order — add / drop items, change qty or rate"
                    >
                      <span className="inline-flex items-center gap-1"><Pencil size={13} /> Edit</span>
                    </Button>
                  )}
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => docViewer.open('sales-order-review', e.id, e.enqNo)}
                    title="Print / view Sales Order receipt"
                  >
                    <span className="inline-flex items-center gap-1"><FileText size={13} /> Receipt</span>
                  </Button>
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => onSoExcel(e.id, e.enqNo)}
                    title="Download Sales Order as Excel"
                  >
                    <span className="inline-flex items-center gap-1"><Download size={13} /> SO Excel</span>
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )
      ) : (
        <Table<Enquiry>
          storageKey="enquiry-review"
          title="Processed Orders"
          count={q ? `Showing ${shownHistory.length} of ${history.length}` : `Showing ${history.length}`}
          rows={shownHistory}
          rowKey={(r) => r.id}
          loading={historyQuery.isFetching && history.length === 0}
          empty={q ? `No orders match “${q}”.` : <><PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No orders have moved past 'new' yet.</>}
          columns={[
            {
              key: 'enqNo',
              header: 'ENQ #',
              minWidth: '130px',
              cell: (r) => <span className="font-extrabold text-accent" title={r.enqNo}>{orderRef(r)}</span>,
            },
            { key: 'date', header: 'Date', minWidth: '105px', cell: (r) => formatDate(r.enqDate) },
            { key: 'party', header: 'Customer', minWidth: '180px', cell: (r) => <span className="font-bold">{r.party.name}</span> },
            { key: 'category', header: 'Category', minWidth: '130px', cell: (r) => r.party.customerType ?? '—' },
            { key: 'salesRep', header: 'Sales Rep', minWidth: '110px', cell: (r) => r.salesRep },
            { key: 'items', header: 'Items', minWidth: '70px', align: 'right', cell: (r) => r.items.length },
            {
              key: 'value',
              header: 'Est. Value',
              minWidth: '120px',
              align: 'right',
              cell: (r) => formatINRSymbol(r.estOrderValue),
            },
            {
              key: 'credit',
              header: 'Credit',
              minWidth: '90px',
              cell: (r) =>
                r.creditStatus ? (
                  <Badge tone={CREDIT_TONE[r.creditStatus]}>{r.creditStatus}</Badge>
                ) : (
                  '—'
                ),
            },
            {
              key: 'stage',
              header: 'Stage',
              minWidth: '120px',
              cell: (r) => (
                <Badge tone={STAGE_TONE[r.stage] ?? 'muted'}>
                  {STAGE_LABEL[r.stage] ?? r.stage}
                </Badge>
              ),
            },
            {
              key: 'actions',
              header: '',
              minWidth: '150px',
              cell: (r) => (
                <div className="flex flex-wrap justify-center gap-1.5">
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => setViewId(r.id)}
                    title="View order details"
                  >
                    <span className="inline-flex items-center gap-1"><Eye size={13} /> View</span>
                  </Button>
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => docViewer.open('sales-order-review', r.id, r.enqNo)}
                    title="Print Sales Order PDF"
                  >
                    <FileText size={13} />
                  </Button>
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => onSoExcel(r.id, r.enqNo)}
                    title="Download Sales Order as Excel"
                  >
                    <Download size={13} />
                  </Button>
                  {REOPENABLE_STAGES.has(r.stage) && (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => onReopen(r)}
                      disabled={reopenMutation.isPending}
                      title={r.stage === 'reviewed' ? 'Clear the approval and send this order back to Pending Review' : 'Void the dispatch order and reopen this order for editing'}
                    >
                      <span className="inline-flex items-center gap-1"><RotateCcw size={13} /> Reopen</span>
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      <EnqReviewModal open={!!reviewId} enqId={reviewId} onClose={() => setReviewId(null)} />
      <EnqReviewModal open={!!viewId} enqId={viewId} onClose={() => setViewId(null)} />
      <EnquiryFormModal
        open={!!editOrder}
        editing={editOrder}
        onClose={() => setEditOrder(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['enquiries'] });
          setEditOrder(null);
        }}
      />
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="font-bold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}
